/**
 * Xenial portal data service
 */
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { Observable } from '@shared/rx';
import { EHeaderName, IApiListResponse, IFilterQuery } from '@shared/types';
import { lazyInject } from '@app/shared/decorators';
import { environment } from '@environments/environment';
import {
  catchHttpErrorAndPickErrorList,
  createRequestParams,
} from '@app/shared/utils/request.utils';
import { IPermissionValue } from '@app/shared/types/portal/permission.type';
import { buildServiceIdentifyContext } from '@app/shared/utils/http-context.utils';
import { ENTITY_TYPES } from '@app/shared/types/enums/entityTypes.enum';

import { TokenSessionService } from '../session/token-session.service';

@Injectable()
export class XeaDataService {
  public static UTILITY_TYPE: string = 'XeaDataService';
  public defaultQuery: IFilterQuery = {};
  // List of available sites for the current employee
  public availableSites: {
    [key: string]: string[];
  } = {};
  public hasMultiStandTransferRequestPermission: boolean = false;

  public get baseUrl(): string {
    return environment.xeaBackendURI;
  }

  @lazyInject(HttpClient) public http: HttpClient;
  @lazyInject(TokenSessionService)
  public tokenSessionService: TokenSessionService;

  private get headers(): HttpHeaders {
    return new HttpHeaders({
      [EHeaderName.Authorization]: `Bearer ${this.tokenSessionService.token}`,
      [EHeaderName.XCompanyId]: this.tokenSessionService.companyId,
    });
  }

  public posLogin(
    url: string,
    pasParam: {
      pos_code: string;
      password?: string;
    },
  ): Observable<any> {
    return this.http.post<null>(`${this.baseUrl}/${url}`, pasParam);
  }

  public getRoleById(roleId: string): Observable<IPermissionValue[]> {
    const basicPath = `${this.baseUrl}/role/${roleId}`;

    return this.http.get(basicPath).pipe(catchHttpErrorAndPickErrorList());
  }

  public getAvailableSitesById(
    query,
  ): Observable<Partial<IApiListResponse<string>>> {
    const basicPath = `${this.baseUrl}/transfer/available-sites`;
    const params = this.createParams(query);

    return this.http
      .get(basicPath, { params })
      .pipe(catchHttpErrorAndPickErrorList());
  }

  /**
   * Method which creates HttpParams and returns
   */
  public createParams(query: IFilterQuery = {}): HttpParams {
    return createRequestParams({
      ...query,
    });
  }

  public fetchCurrentList(query?: IFilterQuery): Observable<IApiListResponse> {
    const basicPath = `${this.baseUrl}/xdm/inventory-item`;

    const params = this.createParams(query);
    return this.http
      .get(basicPath, {
        params,
        context: buildServiceIdentifyContext(ENTITY_TYPES.INVENTORY_ITEM_VENUE),
      })
      .pipe(catchHttpErrorAndPickErrorList());
  }

  public getSitesPreferencesBySiteIds(
    siteIds: string[],
  ): Observable<IApiListResponse> {
    return this.http
      .get(`${this.baseUrl}/xdm/preferences/site?site_ids=${siteIds.join(',')}`)
      .pipe(catchHttpErrorAndPickErrorList());
  }
}
